import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type SubscriptionTier = 'free' | 'core' | 'pro' | 'plus';
type AnyTier = SubscriptionTier | 'basic';

export interface SubscriptionState {
  tier: SubscriptionTier;            // effective tier (trial overrides base)
  baseTier: SubscriptionTier;        // base plan after trial expires
  subscribed: boolean;
  subscriptionEnd: string | null;
  trialEndDate: string | null;
  trialTier: SubscriptionTier | null;
  isOnTrial: boolean;
  isBetaUser: boolean;
  loading: boolean;
  error: string | null;
}

export const normalizeTier = (t: AnyTier | string | null | undefined): SubscriptionTier => {
  if (t === 'basic') return 'core';
  if (t === 'core' || t === 'pro' || t === 'plus' || t === 'free') return t;
  return 'free';
};

const initialState: SubscriptionState = {
  tier: 'free',
  baseTier: 'free',
  subscribed: false,
  subscriptionEnd: null,
  trialEndDate: null,
  trialTier: null,
  isOnTrial: false,
  isBetaUser: false,
  loading: true,
  error: null,
};

// Tier configuration with pricing and features
export const TIER_CONFIG = {
  free: { name: 'Free', price: 0, priceLabel: 'Free', tagline: 'Try the radar.', features: ['5 signals per day', '24-hour delay on new signals', 'Basic radar view'] },
  core: { name: 'Core', price: 9, priceLabel: '$9/month', tagline: 'Real-time signal feed.', features: ['Full real-time signal feed', 'Verified signals only', 'Full radar view', 'Weekly digest email'] },
  pro:  { name: 'Pro', price: 15, priceLabel: '$15/month', tagline: 'Go deeper on what matters.', features: ['Everything in Core', '10 Deep Dive analyses / month', 'Filters: company, indication', 'Bookmark & track signals'] },
  plus: { name: 'Plus', price: 24, priceLabel: '$24/month', tagline: 'Full intelligence platform.', features: ['Everything in Pro', 'Unlimited Deep Dive analyses', 'Email & push alerts', 'Competitive Intelligence reports', 'Advanced radar with cluster analysis'] },
} as const;

export function hasTierAccess(userTier: SubscriptionTier, requiredTier: SubscriptionTier): boolean {
  const order: SubscriptionTier[] = ['free', 'core', 'pro', 'plus'];
  return order.indexOf(userTier) >= order.indexOf(requiredTier);
}

export function resolveEffectiveTier(baseTier: SubscriptionTier, trialTier: SubscriptionTier | null, trialEnd: string | null): { effective: SubscriptionTier; isOnTrial: boolean } {
  if (trialTier && trialEnd && new Date(trialEnd) > new Date()) {
    // Trial overrides plan while active
    return { effective: trialTier, isOnTrial: true };
  }
  return { effective: baseTier, isOnTrial: false };
}

export function useSubscription() {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState<SubscriptionState>(initialState);

  const loadCachedTier = useCallback(async () => {
    if (!user) return false;
    try {
      const { data, error } = await supabase
        .from('user_subscriptions_public')
        .select('tier, status, current_period_end, trial_end_date, trial_tier, is_beta_user')
        .eq('user_id', user.id)
        .maybeSingle() as any;

      if (error || !data) return false;

      const baseTier = normalizeTier(data.tier);
      const trialTier = data.trial_tier ? normalizeTier(data.trial_tier) : null;
      const { effective, isOnTrial } = resolveEffectiveTier(baseTier, trialTier, data.trial_end_date);

      setState({
        tier: effective,
        baseTier,
        subscribed: data.status === 'active' && effective !== 'free',
        subscriptionEnd: data.current_period_end || null,
        trialEndDate: data.trial_end_date || null,
        trialTier,
        isOnTrial,
        isBetaUser: !!data.is_beta_user,
        loading: false,
        error: null,
      });
      return true;
    } catch {
      return false;
    }
  }, [user]);

  const checkSubscription = useCallback(async () => {
    if (!user) {
      setState({ ...initialState, loading: false });
      return;
    }

    setState(prev => prev.tier === 'free' && !prev.subscribed
      ? { ...prev, loading: true, error: null }
      : { ...prev, error: null }
    );

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;

      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      });
      if (error) throw error;

      // After Stripe sync, re-read DB so we get trial fields too
      await loadCachedTier();

      // If loadCachedTier didn't run (no row yet), fall back to response
      if (!data) return;
    } catch (err) {
      console.error('Error checking subscription:', err);
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to check subscription',
      }));
    }
  }, [user, loadCachedTier]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setState({ ...initialState, loading: false });
      return;
    }

    let cancelled = false;
    (async () => {
      const loaded = await loadCachedTier();
      if (!cancelled && !loaded) {
        await checkSubscription();
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, user, loadCachedTier, checkSubscription]);

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(checkSubscription, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, checkSubscription]);

  const createCheckout = async (tier: 'core' | 'pro' | 'plus', options?: { trial?: boolean }) => {
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { tier, trial: options?.trial },
      });
      if (error) throw error;
      if (data.url) window.open(data.url, '_blank');
    } catch (err) {
      console.error('Error creating checkout:', err);
      throw err;
    }
  };

  const openCustomerPortal = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      if (error) throw error;
      if (data.url) window.open(data.url, '_blank');
    } catch (err) {
      console.error('Error opening customer portal:', err);
      throw err;
    }
  };

  return {
    ...state,
    checkSubscription,
    createCheckout,
    openCustomerPortal,
    hasTierAccess: (requiredTier: SubscriptionTier) => hasTierAccess(state.tier, requiredTier),
  };
}
